import { useEffect, useState, useCallback, useRef } from "react"
import Swal from "sweetalert2"
import Cookies from "js-cookie"
import * as XLSX from "xlsx"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
    faX,
    faFolder,
    faCog,
    faSignOutAlt,
    faBars,
    faUserCircle,
    faBuilding,
    faCalendarAlt,
    faEye,
    faFilter,
    faChartBar,
    faChartPie,
} from "@fortawesome/free-solid-svg-icons"
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom"
import { useLocation } from "react-router-dom"

import { exportMultipleChartsToPdf, formatDate4, getOrDeleteRequest, getSession, postOrPutRequest, serializeExportData } from "../utils/utils"
import SelectMultiple from "../components/SelectMultiple"
import ChartComponent from "../components/ChartComponent/ChartComponent"
import { usePanel } from "../components/PanelContext/PanelContext"

let html2canvas, jsPDF

const loadPDFLibraries = async () => {
    try {
        if (typeof window !== "undefined") {
            const html2canvasModule = await import("html2canvas")
            const jsPDFModule = await import("jspdf")
            html2canvas = html2canvasModule.default
            jsPDF = jsPDFModule.default
            return true
        }
        return false
    } catch (error) {
        console.error("Error cargando librerías PDF:", error)
        return false
    }
}

const MainThemeLayoutCompany = ({ children, cards, data = [] }) => {
    const location = useLocation()
    const navigate = useNavigate()
    const { idCompany, idProject } = useParams()
    const [searchParams, setSearchParams] = useSearchParams()
    const [companies, setCompanies] = useState([])
    const [pdfLibrariesLoaded, setPdfLibrariesLoaded] = useState(false)

    const { isPanelVisible, togglePanelVisibility } = usePanel()

    const [panel, setPanel] = useState(false)
    const [reports, setReports] = useState([])
    const [loading, setLoading] = useState(true)
    const [showFiltersMobile, setShowFiltersMobile] = useState(false)
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
    const [userInfo, setUserInfo] = useState({
        userName: "",
        role: "",
        company: "",
    })
    const [projects, setProjects] = useState([])
    const [resultsGrouped, setResultsGrouped] = useState([])
    const [filters, setFilters] = useState({
        sectors: [],
        companyTypes: [],
        states: [],
        cities: [],
        districts: [],
    })

    const [tempDateType, setTempDateType] = useState("single")
    const [tempSelectedMonth, setTempSelectedMonth] = useState("")
    const [tempSelectedYear, setTempSelectedYear] = useState("")
    const [tempRangeStartMonth, setTempRangeStartMonth] = useState("")
    const [tempRangeStartYear, setTempRangeStartYear] = useState("")
    const [tempRangeEndMonth, setTempRangeEndMonth] = useState("")
    const [tempRangeEndYear, setTempRangeEndYear] = useState("")

    const [activeDateType, setActiveDateType] = useState("single")
    const [activeDateParams, setActiveDateParams] = useState({})
    const [tempSectorId, setTempSectorId] = useState("")
    const [tempCompanyTypeId, setTempCompanyTypeId] = useState("")
    const [tempStateId, setTempStateId] = useState("")
    const [tempCityId, setTempCityId] = useState("")
    const [tempDistrictId, setTempDistrictId] = useState("")

    const [activeFilters, setActiveFilters] = useState({})

    const [fileName, setFileName] = useState(null)
    const chartComponentRef = useRef()

    useEffect(() => {
        loadPDFLibraries().then((loaded) => {
            setPdfLibrariesLoaded(loaded)
        })
    }, [])

    const fetchReportData = async () => {
        try {
            if (!idProject) {
                return null
            }

            const session = getSession()
            if (!session) return null

            const response = await getOrDeleteRequest(`report-details/by-project/${idProject}`, "GET", null, false)

            if (response.type === "success") {
                return response.data
            } else {
                return null
            }
        } catch (error) {
            return null
        }
    }

    const transformReportDataForExport = (reportData) => {
        if (!reportData || typeof reportData !== "object" || Object.keys(reportData).length === 0) {
            return []
        }

        const exportData = []

        try {
            Object.entries(reportData).forEach(([fecha, empresasData]) => {
                Object.entries(empresasData).forEach(([empresaKey, indicadoresData]) => {
                    const [empresaId, ...nombreParts] = empresaKey.split("__")
                    const empresaNombre = nombreParts.join("__")

                    Object.entries(indicadoresData).forEach(([indicadorKey, datosIndicador]) => {
                        const [indicadorId, indicadorRawName] = indicadorKey.split("__")
                        const indicadorNombre = indicadorRawName.replace(/_/g, " ")

                        const venta_mes_actual = datosIndicador.venta_mes_actual || 0
                        const venta_mes_anterior = datosIndicador.venta_mes_anterior || 0
                        const mes_actual_mc = datosIndicador.mes_actual_mc || 0
                        const mes_anterior_mc = datosIndicador.mes_anterior_mc || 0
                        const agua_mes_actual = datosIndicador.agua_mes_actual || 0
                        const agua_mes_anterior = datosIndicador.agua_mes_anterior || 0
                        const energia_mes_actual = datosIndicador.energia_mes_actual || 0
                        const energia_mes_anterior = datosIndicador.energia_mes_anterior || 0
                        const total_de_kw_mensual = datosIndicador.total_de_kw_mensual || 0
                        const gasolina_antes = datosIndicador.gasolina_antes || 0
                        const gasolina_despues = datosIndicador.gasolina_despues || 0
                        const gas_antes = datosIndicador.gas_antes || 0
                        const gas_despues = datosIndicador.gas_despues || 0
                        const result = datosIndicador.result || 0

                        const rowData = {
                            fecha: formatDate4(fecha),
                            empresa: empresaNombre,
                            id_empresa: empresaId,
                            indicador: indicadorNombre,
                            id_indicador: indicadorId,
                            resultado: result,
                            venta_mes_actual: venta_mes_actual,
                            venta_mes_anterior: venta_mes_anterior,
                            mes_actual_mc: mes_actual_mc,
                            mes_anterior_mc: mes_anterior_mc,
                            agua_mes_actual: agua_mes_actual,
                            agua_mes_anterior: agua_mes_anterior,
                            energia_mes_actual: energia_mes_actual,
                            energia_mes_anterior: energia_mes_anterior,
                            total_de_kw_mensual: total_de_kw_mensual,
                            gasolina_antes: gasolina_antes,
                            gasolina_despues: gasolina_despues,
                            gas_antes: gas_antes,
                            gas_despues: gas_despues,
                        }

                        exportData.push(rowData)
                    })
                })
            })

            return exportData
        } catch (error) {
            return []
        }
    }

    const transformReportDataForCharts = (reportData) => {
        if (!reportData || typeof reportData !== "object" || Object.keys(reportData).length === 0) {
            return {}
        }

        const chartData = {}

        try {
            Object.entries(reportData).forEach(([fecha, empresasData]) => {
                Object.entries(empresasData).forEach(([empresaKey, indicadoresData]) => {
                    const [empresaId, ...nombreParts] = empresaKey.split("__")
                    const empresaNombre = nombreParts.join("__")

                    Object.entries(indicadoresData).forEach(([indicadorKey, datosIndicador]) => {
                        const [indicadorId, indicadorRawName] = indicadorKey.split("__")
                        const cleanIndicatorName = indicadorRawName.replace(/_/g, " ")

                        if (!chartData[cleanIndicatorName]) {
                            chartData[cleanIndicatorName] = []
                        }

                        chartData[cleanIndicatorName].push({
                            date_response: fecha,
                            result: datosIndicador.result || 0,
                            project_name: empresaNombre,
                            project_id: empresaId,
                            venta_mes_actual: datosIndicador.venta_mes_actual || 0,
                            venta_mes_anterior: datosIndicador.venta_mes_anterior || 0,
                            mes_actual_mc: datosIndicador.mes_actual_mc || 0,
                            mes_anterior_mc: datosIndicador.mes_anterior_mc || 0,
                            agua_mes_actual: datosIndicador.agua_mes_actual || 0,
                            agua_mes_anterior: datosIndicador.agua_mes_anterior || 0,
                            energia_mes_actual: datosIndicador.energia_mes_actual || 0,
                            energia_mes_anterior: datosIndicador.energia_mes_anterior || 0,
                            total_de_kw_mensual: datosIndicador.total_de_kw_mensual || 0,
                            gasolina_antes: datosIndicador.gasolina_antes || 0,
                            gasolina_despues: datosIndicador.gasolina_despues || 0,
                            gas_antes: datosIndicador.gas_antes || 0,
                            gas_despues: datosIndicador.gas_despues || 0,
                        })
                    })
                })
            })
            
            return chartData
        } catch (error) {
            console.error("[v0] transformReportDataForCharts error:", error)
            return {}
        }
    }

    const createSummaryData = (exportData) => {
        const summaryMap = {}

        exportData.forEach((row) => {
            const key = `${row.id_empresa}_${row.empresa}`

            if (!summaryMap[key]) {
                summaryMap[key] = {
                    empresa: row.empresa,
                    id_empresa: row.id_empresa,
                    total_ventas: 0,
                    total_agua: 0,
                    total_energia: 0,
                    total_co2: 0,
                    cantidad_indicadores: 0,
                }
            }

            const summary = summaryMap[key]
            summary.cantidad_indicadores++

            if (row.indicador.includes("Ventas")) {
                summary.total_ventas += row.venta_mes_actual || 0
            } else if (row.indicador.includes("agua")) {
                summary.total_agua += row.mes_actual_mc || row.agua_mes_actual || 0
            } else if (row.indicador.includes("energía") || row.indicador.includes("kilo-watts")) {
                summary.total_energia += row.energia_mes_actual || row.total_de_kw_mensual || 0
            } else if (row.indicador.includes("CO2")) {
                summary.total_co2 += row.resultado || 0
            }
        })

        return Object.values(summaryMap)
    }

    const createIndicatorSummary = (exportData) => {
        const indicatorMap = {}

        exportData.forEach((row) => {
            const key = `${row.id_indicador}_${row.indicador}`

            if (!indicatorMap[key]) {
                indicatorMap[key] = {
                    indicador: row.indicador,
                    id_indicador: row.id_indicador,
                    total_resultado: 0,
                    promedio_resultado: 0,
                    cantidad_empresas: 0,
                    empresas_aplicadas: 0,
                }
            }

            const indicator = indicatorMap[key]
            indicator.cantidad_empresas++

            if (row.resultado !== 0) {
                indicator.empresas_aplicadas++
                indicator.total_resultado += row.resultado
            }
        })

        Object.values(indicatorMap).forEach((indicator) => {
            indicator.promedio_resultado =
                indicator.empresas_aplicadas > 0 ? indicator.total_resultado / indicator.empresas_aplicadas : 0
        })

        return Object.values(indicatorMap)
    };

    const exportToExcel = async () => {
        try {
            Swal.fire({
                title: "Generando Excel",
                text: "Obteniendo y organizando datos...",
                icon: "info",
                allowOutsideClick: false,
                showConfirmButton: false,
                didOpen: () => {
                    Swal.showLoading()
                },
            })

            const reportData = await fetchReportData()

            if (!reportData || typeof reportData !== "object" || Object.keys(reportData).length === 0) {
                throw new Error("No se pudieron obtener los datos o los datos están vacíos")
            }

            const exportData = transformReportDataForExport(reportData)

            if (exportData.length === 0) {
                Swal.fire({
                    title: "Sin datos",
                    text: "No hay datos disponibles para exportar",
                    icon: "warning",
                    confirmButtonText: "Aceptar",
                })
                return
            }

            const wb = XLSX.utils.book_new()

            const wsData = XLSX.utils.json_to_sheet(exportData)
            XLSX.utils.book_append_sheet(wb, wsData, "Datos del Proyecto")

            //   const summaryData = createSummaryData(exportData)
            //   if (summaryData.length > 0) {
            //     const wsSummary = XLSX.utils.json_to_sheet(summaryData)
            //     XLSX.utils.book_append_sheet(wb, wsSummary, "Resumen por Empresa")
            //   }

            //   const indicatorSummary = createIndicatorSummary(exportData)
            //   if (indicatorSummary.length > 0) {
            //     const wsIndicator = XLSX.utils.json_to_sheet(indicatorSummary)
            //     XLSX.utils.book_append_sheet(wb, wsIndicator, "Resumen por Indicador")
            //   }

            const colWidths = [
                { wch: 12 },
                { wch: 25 },
                { wch: 8 },
                { wch: 35 },
                { wch: 10 },
                { wch: 15 },
                { wch: 15 },
                { wch: 15 },
                { wch: 15 },
                { wch: 15 },
                { wch: 15 },
                { wch: 15 },
                { wch: 15 },
                { wch: 15 },
                { wch: 20 },
                { wch: 15 },
                { wch: 15 },
                { wch: 15 },
                { wch: 15 },
            ]

            wsData["!cols"] = colWidths

            const companyName = companies.find((company) => company.id == idCompany)?.name || "Empresa"
            const projectName = projects.find((project) => project.id == idProject)?.name || "Proyecto"
            const fileName = `reporte_${companyName.replace(/\s+/g, "_")}_${projectName.replace(/\s+/g, "_")}_${new Date().toISOString().split("T")[0]}.xlsx`

            XLSX.writeFile(wb, fileName)

            Swal.fire({
                title: "Excel Generado",
                text: `Archivo exportado exitosamente con ${exportData.length} registros`,
                icon: "success",
                confirmButtonText: "Aceptar",
                timer: 3000,
            })
        } catch (error) {
            Swal.fire({
                title: "Error",
                text: `No se pudo generar el archivo Excel: ${error.message}`,
                icon: "error",
                confirmButtonText: "Aceptar",
            })
        }
    };

    const generateStatisticsSummary = (chartData) => {
        const statistics = {
            totalIndicators: 0,
            indicatorsWithData: 0,
            averagePerformance: 0,
            topPerformers: [],
            needsImprovement: [],
            trends: [],
            dataSummary: {},
        }

        if (!chartData || typeof chartData !== "object") {
            return statistics
        }

        Object.entries(chartData).forEach(([indicatorName, dataArray]) => {
            if (Array.isArray(dataArray) && dataArray.length > 0) {
                statistics.totalIndicators++

                const validData = dataArray.filter(
                    (item) => item.result !== null && item.result !== undefined && !isNaN(Number.parseFloat(item.result)),
                )

                if (validData.length > 0) {
                    statistics.indicatorsWithData++

                    const values = validData.map((item) => Number.parseFloat(item.result))
                    const sum = values.reduce((a, b) => a + b, 0)
                    const avg = sum / values.length
                    const max = Math.max(...values)
                    const min = Math.min(...values)
                    const latest = values[values.length - 1]

                    statistics.dataSummary[indicatorName] = {
                        average: avg,
                        max: max,
                        min: min,
                        latest: latest,
                        dataPoints: validData.length,
                    }

                    if (validData.length >= 2) {
                        const latestValue = Number.parseFloat(validData[validData.length - 1].result)
                        const previousValue = Number.parseFloat(validData[validData.length - 2].result)
                        const change = previousValue !== 0 ? ((latestValue - previousValue) / Math.abs(previousValue)) * 100 : 0

                        const trend = change > 5 ? "up" : change < -5 ? "down" : "stable"

                        statistics.trends.push({
                            indicator: indicatorName,
                            current: latestValue,
                            previous: previousValue,
                            trend: trend,
                            change: Math.abs(change).toFixed(1),
                        })
                    }
                }
            }
        })

        return statistics
    };

    const captureChartsWithRetry = async (maxRetries = 3) => {
        for (let attempt = 1; attempt <= maxRetries; attempt++) {
            try {
                await new Promise((resolve) => setTimeout(resolve, 2000))
                window.dispatchEvent(new Event("resize"))
                await new Promise((resolve) => setTimeout(resolve, 1000))

                const chartContainer =
                    document.querySelector(".mt-5.p-5") ||
                    document.querySelector("#Reportes") ||
                    document.querySelector(".chart-container") ||
                    document.querySelector(".flex-1.p-4.relative")

                if (!chartContainer) {
                    throw new Error("No se encontró el contenedor de gráficos")
                }

                const canvas = await html2canvas(chartContainer, {
                    scale: 2,
                    useCORS: true,
                    allowTaint: true,
                    backgroundColor: "#ffffff",
                    logging: false,
                    width: chartContainer.scrollWidth,
                    height: chartContainer.scrollHeight,
                })

                if (canvas && canvas.width > 0 && canvas.height > 0) {
                    return canvas
                }
            } catch (error) {
                if (attempt === maxRetries) {
                    throw error
                }
                await new Promise((resolve) => setTimeout(resolve, 1000))
            }
        }
        throw new Error("No se pudieron capturar los gráficos después de todos los intentos")
    };

    const exportChartsToPDF = async () => {
        try {
            Swal.fire({
                title: "Generando Reporte PDF",
                text: "Obteniendo datos y preparando gráficos...",
                icon: "info",
                allowOutsideClick: false,
                showConfirmButton: false,
                didOpen: () => {
                    Swal.showLoading()
                },
            })
            
            const companyName = companies.find((company) => company.id == idCompany)?.name || ""
            const projectName = projects.find((project) => project.id == idProject)?.name || ""
            const currentDate = new Date()
            
            const fileName = `reporte-${companyName.replace(/\s+/g, "-")}-${projectName.replace(/\s+/g, "-")}-${currentDate.toISOString().split("T")[0]}.pdf`;
            
            await exportMultipleChartsToPdf({fileName});

            Swal.fire({
                title: "PDF Generado",
                text: "Reporte exportado exitosamente con gráficos",
                icon: "success",
                confirmButtonText: "Aceptar",
                timer: 3000,
            })
        } catch (error) {
            Swal.fire({
                title: "Error",
                text: "No se pudo generar el reporte PDF. Intente nuevamente.",
                icon: "error",
                confirmButtonText: "Aceptar",
            })
        }
    };

    const getRibbon = async () => {
        const response = await getOrDeleteRequest(`ribbon/filter`, "GET", null, false)
        if (response.type === "success") {
            if (response.data.length > 0) {
                const images = response.data.filter((item) => item.place === "Sistema Inicio").map((item) => item.image)

                setFileName(images.length > 0 ? images[0] : null)
            } else {
                setFileName(null)
            }
            return
        }
    };

    useEffect(() => {
        const getValidParam = (param) => {
            const value = searchParams.get(param)
            return value && value.trim() !== "" ? value : ""
        }

        const dateSelect = getValidParam("date_select")
        const dateStart = getValidParam("date_start")
        const dateEnd = getValidParam("date_end")
        const type = getValidParam("date_type") || "single"
        const sectorId = getValidParam("sector_id")
        const companyTypeId = getValidParam("company_type_id")
        const stateId = getValidParam("state_id")
        const cityId = getValidParam("city_id")
        const districtId = getValidParam("district_id")

        setActiveDateType(type)
        setTempDateType(type)

        if (type === "single" && dateSelect) {
            const [year, month] = dateSelect.split("-")
            setTempSelectedYear(year)
            setTempSelectedMonth(month)
            setActiveDateParams({
                date_select: `${year}-${month}-01`,
            })
        } else if (type === "range") {
            if (dateStart) {
                const [startYear, startMonth] = dateStart.split("-")
                setTempRangeStartYear(startYear)
                setTempRangeStartMonth(startMonth)
            }
            if (dateEnd) {
                const [endYear, endMonth] = dateEnd.split("-")
                setTempRangeEndYear(endYear)
                setTempRangeEndMonth(endMonth)
            }
            setActiveDateParams({
                date_start: dateStart ? `${dateStart}-01` : "",
                date_end: dateEnd ? `${dateEnd}-01` : "",
            })
        }

        setTempSectorId(sectorId)
        setTempCompanyTypeId(companyTypeId)
        setTempStateId(stateId)
        setTempCityId(cityId)
        setTempDistrictId(districtId)
        setActiveFilters({
            sector_id: sectorId,
            company_type_id: companyTypeId,
            state_id: stateId,
            city_id: cityId,
            district_id: districtId,
        })
    }, [searchParams])

    const generateYears = useCallback(() => {
        const currentYear = new Date().getFullYear()
        return Array.from({ length: 10 }, (_, i) => currentYear - i)
    }, [])

    const months = [
        { value: "01", label: "Enero" },
        { value: "02", label: "Febrero" },
        { value: "03", label: "Marzo" },
        { value: "04", label: "Abril" },
        { value: "05", label: "Mayo" },
        { value: "06", label: "Junio" },
        { value: "07", label: "Julio" },
        { value: "08", label: "Agosto" },
        { value: "09", label: "Septiembre" },
        { value: "10", label: "Octubre" },
        { value: "11", label: "Noviembre" },
        { value: "12", label: "Diciembre" },
    ]

    const formatDateForBackend = (year, month) => {
        return year && month ? `${year}-${month}-01` : ""
    }

    const loadFilterOptions = useCallback(async () => {
        const session = getSession()
        if (!session) return

        try {
            if ([1, 6].includes(getSession()?.type_id)) {
                const sectorsResponse = await getOrDeleteRequest("business_sectors/filter", "GET")
                if (sectorsResponse.type === "success") {
                    setFilters((prev) => ({
                        ...prev,
                        sectors: sectorsResponse.data,
                    }))
                }

                const companyTypesResponse = await getOrDeleteRequest("company-type/filter", "GET")
                if (companyTypesResponse.type === "success") {
                    setFilters((prev) => ({
                        ...prev,
                        companyTypes: companyTypesResponse.data,
                    }))
                }

                const statesResponse = await getOrDeleteRequest("states/filter", "GET")
                if (statesResponse.type === "success") {
                    setFilters((prev) => ({
                        ...prev,
                        states: statesResponse.data,
                    }))
                }
            }

            if (tempStateId && [1, 6].includes(getSession()?.type_id)) {
                const citiesResponse = await getOrDeleteRequest(`cities/by-state/${tempStateId}`, "GET")
                if (citiesResponse.type === "success") {
                    setFilters((prev) => ({
                        ...prev,
                        cities: citiesResponse.data,
                    }))
                }
            }

            if (tempCityId && [1, 6].includes(session.type_id)) {
                const districtsResponse = await getOrDeleteRequest(`districts/by-city/${tempCityId}`, "GET")
                if (districtsResponse.type === "success") {
                    setFilters((prev) => ({
                        ...prev,
                        districts: districtsResponse.data,
                    }))
                }
            }
        } catch (error) {
            console.error("Error loading filter options:", error)
        }
    }, [tempStateId, tempCityId])

    useEffect(() => {
        loadFilterOptions()
    }, [loadFilterOptions]);

    const loadExportData = useCallback(async () => {
        // console.log("Loading export data for company and project:", idCompany, idProject);
        let session = getSession();
        // console.log("Current session:", session);

        let uri = `report-details/by-project-by-company/${idProject}?company_id=${session.type_id === 4 ? session.id : idCompany}`;

        let response = await getOrDeleteRequest(
            uri, 
            'GET',
            null,
            false
        );

        // console.log("Fetch export data URI:", uri);

        // console.log("Export data response:", response);

        if (response.type === 'success') {
            let dataForExport = serializeExportData(response.data);
            // console.log('Data prepared for export:', dataForExport);
            let dashboardData = JSON.parse(localStorage.getItem('dashboardToCompany')) || {};

            localStorage.setItem('dashboardToCompany', JSON.stringify(dataForExport));
            return;
        }

        localStorage.setItem('dashboardToCompany', JSON.stringify({}));
        return;
    }, [idCompany, idProject]);

    const loadDashboardData = useCallback(async () => {
        setLoading(true)
        const session = getSession()
        if (!session) return

        try {
            const companyResponse = await getOrDeleteRequest("company/filter", "GET")
            if (companyResponse.type === "success") {
                setCompanies(
                    companyResponse.data.length > 0
                        ? companyResponse.data.map((item) => {
                            return {
                                id: item.id,
                                name: `${item.company_name} ${item.trade_name ? `(${item.trade_name})` : ""}`,
                            }
                        })
                        : [],
                )
            }

            const company = companyResponse.data.find((item) => item.id == idCompany)
            if ([1, 4, 6].includes(session.type_id)) {
                const projectsUri = `projects/projectbycompany/${company.id}`

                const projectsResponse = await getOrDeleteRequest(projectsUri, "GET")
                if (projectsResponse.type === "success") {
                    setProjects(projectsResponse.data)
                }
            }

            let reportsUri = `reports/list-report/admin/${company.user_id}?page=1&limit=5&company_id=${company.id}`

            if (idProject) reportsUri += `&project_id=${idProject}`

            const queryParams = new URLSearchParams()

            if (activeDateParams.date_select) {
                queryParams.append("date_select", activeDateParams.date_select)
            }
            if (activeDateParams.date_start) {
                queryParams.append("date_start", activeDateParams.date_start)
            }
            if (activeDateParams.date_end) {
                queryParams.append("date_end", activeDateParams.date_end)
            }

            if ([1, 6].includes(session.type_id)) {
                if (activeFilters.sector_id) queryParams.append("sector_id", activeFilters.sector_id)
                if (activeFilters.company_type_id) queryParams.append("company_type_id", activeFilters.company_type_id)
                if (activeFilters.state_id) queryParams.append("state_id", activeFilters.state_id)
                if (activeFilters.city_id) queryParams.append("city_id", activeFilters.city_id)
                if (activeFilters.district_id) queryParams.append("district_id", activeFilters.district_id)
            }

            const reportsResponse = await getOrDeleteRequest(reportsUri, "GET")
            if (reportsResponse.type === "success") {
                setReports(reportsResponse.data.data)
            }

            let indicatorsUri = `dashboard/indicators-by-company/${company.user_id}`

            if (idProject) indicatorsUri += `?project_id=${idProject}`

            if (queryParams.toString()) {
                indicatorsUri += idProject ? `&${queryParams.toString()}` : `?${queryParams.toString()}`
            }

            const indicatorsResponse = await getOrDeleteRequest(indicatorsUri, "GET")
            if (indicatorsResponse.type === "success") {
                setResultsGrouped(indicatorsResponse.results_grouped || indicatorsResponse.data || {})

                const dashboardData = JSON.parse(localStorage.getItem("dashboardData")) || {}
                const central = dashboardData.central || {}
                localStorage.setItem(
                    "dashboardData",
                    JSON.stringify({
                        central: central,
                        right: indicatorsResponse.results_grouped || indicatorsResponse.data || {},
                    }),
                )
            }

            await loadExportData();
        } catch (error) {
            console.error("Error loading dashboard data:", error)
        } finally {
            setLoading(false)
        }
    }, [idCompany, activeDateParams, activeFilters, idProject])

    useEffect(() => {
        loadDashboardData()
    }, [loadDashboardData])

    useEffect(() => {
        const loadDataWithParams = async () => {
            await loadDashboardData()
        }

        loadDataWithParams()
    }, [idCompany, idProject, searchParams])

    const handleStateChange = async (stateId) => {
        setTempStateId(stateId)
        setTempCityId("")
        setTempDistrictId("")

        if (stateId && [1, 6].includes(getSession()?.type_id)) {
            const citiesResponse = await getOrDeleteRequest(`cities/by-state/${stateId}`, "GET")
            if (citiesResponse.type === "success") {
                setFilters((prev) => ({
                    ...prev,
                    cities: citiesResponse.data,
                    districts: [],
                }))
            }
        } else {
            setFilters((prev) => ({
                ...prev,
                cities: [],
                districts: [],
            }))
        }
    };

    const handleCityChange = async (cityId) => {
        setTempCityId(cityId)
        setTempDistrictId("")

        const session = getSession()
        if (cityId && [1, 6].includes(session?.type_id)) {
            const districtsResponse = await getOrDeleteRequest(`districts/by-city/${cityId}`, "GET")
            if (districtsResponse.type === "success") {
                setFilters((prev) => ({
                    ...prev,
                    districts: districtsResponse.data,
                }))
            }
        } else {
            setFilters((prev) => ({
                ...prev,
                districts: [],
            }))
        }
    };

    useEffect(() => {
        if (location.pathname === "/PanelDeControl" || location.pathname.startsWith("/PanelDeControlEmpresa/")) {
            setPanel(true)
        } else {
            setPanel(false)
        }
    }, [location.pathname])

    const handleLogout = async () => {
        const response = await postOrPutRequest("auth/logout", "POST", {})

        if (response.type === "success") {
            Cookies.remove(process.env.REACT_APP_TOKEN)
            Cookies.remove(process.env.REACT_APP_USER)

            Swal.fire({
                title: "Sesión cerrada",
                text: response.message,
                icon: "success",
                confirmButtonText: "Aceptar",
                timer: 3000,
            }).then(() => {
                window.location.href = "/"
            })
        } else {
            Swal.fire({
                title: "Error!",
                text: response.message,
                icon: "error",
                confirmButtonText: "Aceptar",
            })
        }
    }

    const applyDateFilters = () => {
        const params = {}
        const session = getSession()
        const isAdmin = session && [1, 6].includes(session.type_id)

        const addParamIfValid = (key, value) => {
            if (value !== undefined && value !== null && value !== "") {
                params[key] = value
            }
        }

        if (tempDateType === "single") {
            addParamIfValid("date_type", "single")
            if (tempSelectedMonth && tempSelectedYear) {
                const dateSelect = `${tempSelectedYear}-${tempSelectedMonth}`
                addParamIfValid("date_select", dateSelect)
                setActiveDateParams({
                    date_select: formatDateForBackend(tempSelectedYear, tempSelectedMonth),
                })
            }
        } else if (tempDateType === "range") {
            addParamIfValid("date_type", "range")

            if (tempRangeStartYear && tempRangeStartMonth) {
                const dateStart = `${tempRangeStartYear}-${tempRangeStartMonth}`
                addParamIfValid("date_start", dateStart)
            }

            if (tempRangeEndYear && tempRangeEndMonth) {
                const dateEnd = `${tempRangeEndYear}-${tempRangeEndMonth}`
                addParamIfValid("date_end", dateEnd)
            }

            setActiveDateParams({
                date_start: formatDateForBackend(tempRangeStartYear, tempRangeStartMonth),
                date_end: formatDateForBackend(tempRangeEndYear, tempRangeEndMonth),
            })
        }

        if (isAdmin) {
            addParamIfValid("sector_id", tempSectorId)
            addParamIfValid("company_type_id", tempCompanyTypeId)
            addParamIfValid("state_id", tempStateId)
            addParamIfValid("city_id", tempCityId)
            addParamIfValid("district_id", tempDistrictId)
        }

        setActiveDateType(tempDateType)
        setActiveFilters({
            sector_id: tempSectorId,
            company_type_id: tempCompanyTypeId,
            state_id: tempStateId,
            city_id: tempCityId,
            district_id: tempDistrictId,
        })

        const targetUrl = idCompany
            ? `/PanelDeControlEmpresa/${idCompany}${idProject ? `/${idProject}` : ""}`
            : "/PanelDeControl"

        const queryParams = new URLSearchParams()
        Object.entries(params).forEach(([key, value]) => {
            if (value !== undefined && value !== null && value !== "") {
                queryParams.append(key, value)
            }
        })

        navigate(`${targetUrl}${queryParams.toString() ? `?${queryParams.toString()}` : ""}`)
    }

    const handleProjectChange = (projectId) => {
        if (projectId === idProject) return

        const currentParams = new URLSearchParams()
        const validParams = [
            "date_type",
            "date_select",
            "date_start",
            "date_end",
            "sector_id",
            "company_type_id",
            "state_id",
            "city_id",
            "district_id",
        ]

        validParams.forEach((param) => {
            const value = searchParams.get(param)
            if (value && value.trim() !== "") {
                currentParams.append(param, value)
            }
        })

        const targetUrl = projectId ? `/PanelDeControlEmpresa/${idCompany}/${projectId}` : "/PanelDeControl"
        navigate(`${targetUrl}${currentParams.toString() ? `?${currentParams.toString()}` : ""}`)
    }

    const clearFilters = () => {
        setTempSelectedMonth("")
        setTempSelectedYear("")
        setTempRangeStartMonth("")
        setTempRangeStartYear("")
        setTempRangeEndMonth("")
        setTempRangeEndYear("")
        setTempDateType("single")
        setActiveDateParams({})
        setActiveDateType("single")

        setTempSectorId("")
        setTempCompanyTypeId("")
        setTempStateId("")
        setTempCityId("")
        setTempDistrictId("")
        setActiveFilters({})

        setSearchParams({})

        navigate("/PanelDeControl", { replace: true })
    }

    const toggleMobileMenu = () => {
        setIsMobileMenuOpen(!isMobileMenuOpen)
        if (!isMobileMenuOpen) {
            document.body.style.overflow = "hidden"
        } else {
            document.body.style.overflow = "auto"
        }
    }

    const toggleFiltersMobile = () => {
        setShowFiltersMobile(!showFiltersMobile)
        if (!showFiltersMobile) {
            document.body.style.overflow = "hidden"
        } else {
            document.body.style.overflow = "auto"
        }
    }

    const loadUserData = useCallback(() => {
        const session = getSession()
        if (!session) return

        let userRole = "No especificado"
        if (session.type_id === 1 || session.type_id === 6) userRole = "Administrador"
        if (session.type_id === 4) userRole = "Organización"

        setUserInfo({
            userName: session.type_id === 4 ? session?.company.company_name : session.username,
            role: userRole,
            company: session.type_id === 4 ? session.company_name : "No aplica",
        })
    }, [])

    useEffect(() => {
        loadUserData()
        return () => {
            document.body.style.overflow = "auto"
        }
    }, [loadUserData])

    const getReport = async (data) => {
        const session = getSession()

        const id = data.id.split("/")[0]
        const date = data.id.split("/")[1]
        const month = date.split("-")[0]
        const year = date.split("-")[1]

        let uri

        if ([1, 6].includes(session.type_id)) {
            uri = `reports/export-report/${data.company.user_id}?month=${month}&year=${year}&project_id=${id}`
        }

        if ([4, 5].includes(session.type_id)) {
            uri = `reports/export-report/${session.type_id === 4 ? session.id : session.company.user_id}?month=${month}&year=${year}&project_id=${id}`
        }

        window.open(`${process.env.REACT_APP_API_URI}${uri}`, "_blank")
    };

    const exportProjectDataToExcel = () => {
        let data = JSON.parse(localStorage.getItem('dashboardToCompany')) || {};
        let session = getSession();

        // console.log("Exporting project data to Excel for session:", session);
        
        try {
            Swal.fire({
                title: "Generando Excel",
                text: "Obteniendo y organizando datos...",
                icon: "info",
                allowOutsideClick: false,
                showConfirmButton: false,
                didOpen: () => {
                    Swal.showLoading()
                },
            })
    
            if (!data || typeof data !== "object" || Object.keys(data).length === 0) {
                throw new Error("No se pudieron obtener los datos o los datos están vacíos")
            }
    
            let filteredData = data;
            
            if (session?.type_id === 4) {
                filteredData = data.filter(row => {
                    const rowCompanyId = row.id_empresa || row.company_id || row.empresa_id;
                    return rowCompanyId == session.id;
                });
                
                // console.log(`Empresa ${session.id}: Filtrando datos, ${filteredData.length} registros de ${data.length} totales`);
            }
    
            if (filteredData.length < 1) {
                Swal.fire({
                    title: "Sin datos",
                    text: "No hay datos disponibles para exportar",
                    icon: "warning",
                    confirmButtonText: "Aceptar",
                    timer: 3000,
                })
                return;
            }
    
            const wb = XLSX.utils.book_new();
            const wsData = XLSX.utils.json_to_sheet(filteredData);
    
            const colWidths = Object.keys(filteredData[0]).map((key) => ({
                wch: Math.max(
                    key.length,
                    ...filteredData.map((row) =>
                        row[key] ? row[key].toString().length : 0
                    )
                ) + 2,
            }));
            wsData["!cols"] = colWidths;
    
            XLSX.utils.book_append_sheet(wb, wsData, "Datos de la empresa");
    
            let fileName;
            if (session?.type_id === 4) {
                fileName = `reporte_empresa_${session.id}_${new Date().toISOString().split("T")[0]}.xlsx`;
            } else {
                fileName = `reporte_completo_${new Date().toISOString().split("T")[0]}.xlsx`;
            }
    
            XLSX.writeFile(wb, fileName);
    
            Swal.fire({
                title: "Excel Generado",
                text: `Archivo exportado exitosamente con ${filteredData.length} registros`,
                icon: "success",
                confirmButtonText: "Aceptar",
                timer: 3000,
            });
        } catch (error) {
            Swal.fire({
                title: "Error",
                text: `No se pudo generar el archivo Excel: ${error.message}`,
                icon: "error",
                confirmButtonText: "Aceptar",
            })
        }
        return;
    };

    useEffect(() => {
        getRibbon();
    }, []);

    return (
        <div className="flex flex-col lg:flex-row h-screen w-full overflow-hidden dark:bg-[#292929]">
            <nav
                id="Organización"
                className="hidden lg:flex lg:w-1/6 w-full h-full pt-7 pb-5 px-4 flex-col bg-white dark:bg-[#292929]"
            >
                <div className="flex flex-col h-full justify-between overflow-y-auto">
                    <div>
                        <div className="flex flex-row justify-start items-center pb-9">
                            <FontAwesomeIcon
                                icon={faUserCircle}
                                className="text-gray-500 dark:text-white text-2xl rounded-full w-9 h-9 p-1"
                            />
                            <div className="p-1">
                                <span>
                                    <h1 className="text-sm font-medium dark:text-white">{userInfo.userName}</h1>
                                </span>
                                <span>
                                    <h1 className="text-xs dark:text-stone-400">{userInfo.role === "Organización" ? "Empresa" : userInfo.role }</h1>
                                </span>
                            </div>
                        </div>
                        <div className="flex flex-col justify-center">
                            <Link to="/PanelDeControl">
                                <h1 className="text-xs font-medium flex items-end mb-1 w-full text-gray-500 bg-transparent py-2 px-2 rounded-lg cursor-pointer transition-colors dark:text-stone-400 hover:bg-turquesa hover:text-white">
                                    <FontAwesomeIcon icon={faChartPie} className="text-xl pr-2 dark:text-white" />
                                    Dashboard
                                </h1>
                            </Link>
                            {userInfo.role === "Administrador" && (
                                <Link to="/Organizaciones">
                                    <h1 className="text-xs font-medium flex items-end mb-1 w-full text-gray-500 bg-transparent py-2 px-2 rounded-lg cursor-pointer transition-colors dark:text-stone-400 hover:bg-turquesa hover:text-white">
                                        <FontAwesomeIcon icon={faBuilding} className="text-xl pr-2 dark:text-white" />
                                        Organizaciones
                                    </h1>
                                </Link>
                            )}

                            {userInfo.role === "Organización" && (
                                <Link to="/Empleados">
                                    <h1 className="text-xs font-medium flex items-end mb-1 w-full text-gray-500 bg-transparent py-2 px-2 rounded-lg cursor-pointer transition-colors dark:text-stone-400 hover:bg-turquesa hover:text-white">
                                        <FontAwesomeIcon icon={faBuilding} className="text-xl pr-2 dark:text-white" />
                                        Usuarios
                                    </h1>
                                </Link>
                            )}
                            <Link to="/Reportes">
                                <h1 className="text-xs font-medium flex items-end mb-1 w-full text-gray-500 bg-transparent py-2 px-2 rounded-lg cursor-pointer transition-colors dark:text-stone-400 hover:bg-turquesa hover:text-white">
                                    <FontAwesomeIcon icon={faFolder} className="text-xl pr-2 dark:text-white" />
                                    Reportes
                                </h1>
                            </Link>
                            {userInfo.role === "Administrador" && (
                                <Link to="/CompararProyectos">
                                    <h1 className="text-xs font-medium flex items-end mb-1 w-full text-gray-500 bg-transparent py-2 px-2 rounded-lg cursor-pointer transition-colors dark:text-stone-400 hover:bg-turquesa hover:text-white">
                                        <FontAwesomeIcon icon={faChartBar} className="text-xl pr-2 dark:text-white" />
                                        Estadísticas de proyectos
                                    </h1>
                                </Link>
                            )}
                        </div>
                    </div>
                    <div className="flex flex-col justify-start">
                        {[4].includes(getSession()?.type_id) && (
                            <Link
                                to="/PerfilDeLaOrganizacion"
                                className="text-xs font-medium items-end mb-1 w-full text-gray-500 bg-transparent py-2 px-2 rounded-lg cursor-pointer transition-colors dark:text-stone-400 hover:bg-orange-500 hover:text-white"
                            >
                                <FontAwesomeIcon icon={faCog} className="text-xl pr-2 dark:text-white" />
                                Ajustes
                            </Link>
                        )}
                        <button type="button" className="text-start" onClick={handleLogout}>
                            <h1 className="text-xs font-medium items-end mb-1 w-full text-gray-500 bg-transparent py-2 px-2 rounded-lg cursor-pointer transition-colors dark:text-stone-400 hover:bg-red-500 hover:text-white">
                                <FontAwesomeIcon icon={faSignOutAlt} className="text-xl pr-2 dark:text-white" />
                                Cerrar sesión
                            </h1>
                        </button>
                    </div>
                </div>
            </nav>

            <section
                id="Graficos"
                className={`${panel && isPanelVisible ? "lg:w-3/6" : "lg:w-5/6"} w-full bg-slate-200 dark:bg-[#1e1e1e] flex flex-col overflow-hidden`}
            >
                {fileName && (
                    <div className="flex-row flex-wrap w-full justify-center items-center gap-2 hidden lg:flex p-6">
                        <div className="w-auto px-1 py-3 bg-white rounded-lg">
                            <img
                                src={`${process.env.REACT_APP_PHOTO_URI}photo/ribbon/image/${fileName}`}
                                className="w-full object-contain"
                                alt=""
                            />
                        </div>
                    </div>
                )}
                <div className="flex items-center justify-between p-2 bg-slate-200 dark:bg-[#1e1e1e]">
                    <button className="p-2 text-gray-500 lg:hidden" onClick={toggleMobileMenu}>
                        <FontAwesomeIcon icon={faBars} className="text-2xl" />
                    </button>

                    {panel && (
                        <div className="flex justify-between w-full p-4">
                            <button className="p-2 text-gray-500 lg:hidden" onClick={toggleFiltersMobile}>
                                <FontAwesomeIcon icon={faFilter} className="text-2xl" />
                            </button>
                            <button
                                className="px-3 py-1 bg-turquesa dark:bg-gray-600 text-white dark:text-white text-sm rounded hidden lg:block cursor-pointer"
                                onClick={togglePanelVisibility}
                            >
                                {isPanelVisible ? "Ocultar filtros" : "Mostrar filtros"}
                            </button>
                        </div>
                    )}
                </div>
                <div
                    className={`flex-1 ${!location.pathname.startsWith("/AgregarReporte") && !location.pathname.startsWith("/EditarReporte") ? "overflow-y-auto" : ""} p-4 relative`}
                >
                    {children}
                </div>
            </section>

            {panel && (isPanelVisible || showFiltersMobile) && (
                <section
                    id="Reportes"
                    className={`lg:w-2/6 w-full flex flex-col bg-white dark:bg-[#292929] overflow-hidden border-l border-gray-200 dark:border-gray-700
                               ${showFiltersMobile ? "fixed flex inset-0 z-50 lg:relative" : "hidden lg:flex"}`}
                >
                    {showFiltersMobile && (
                        <div className="flex justify-end p-2 lg:hidden">
                            <button className="p-2 text-gray-500" onClick={toggleFiltersMobile}>
                                <FontAwesomeIcon icon={faX} className="text-xl" />
                            </button>
                        </div>
                    )}
                    <div className="overflow-y-auto flex-1 p-4">
                        <div className="w-full px-2 flex flex-col gap-2 mb-5">
                            <button
                                disabled={!idProject || !idCompany}
                                // onClick={exportToExcel}
                                onClick={ exportProjectDataToExcel }
                                className="px-3 py-2 bg-green-600 text-white text-sm rounded hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                📊 Exportar a Excel
                            </button>
                            <button
                                onClick={exportChartsToPDF}
                                className="px-3 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                📈 Exportar Gráficos (PDF)
                            </button>
                        </div>
                        {[1, 4, 6].includes(getSession()?.type_id) && (
                            <div className="w-full px-2 space-y-4">
                                <SelectMultiple
                                    name="company_id"
                                    loading={loading}
                                    array={companies}
                                    label="Dashboard por empresa"
                                    isMulti={false}
                                    value={Number(idCompany) ?? ""}
                                    onChange={(e) => {
                                        if (e) {
                                            navigate(`/PanelDeControlEmpresa/${e}`)
                                        } else {
                                            navigate(`/PanelDeControl`)
                                        }
                                    }}
                                    placeholder="Seleccione una empresa"
                                />

                                <SelectMultiple
                                    name="projects"
                                    loading={loading}
                                    array={projects}
                                    label="Proyectos de esta empresa"
                                    isMulti={false}
                                    value={Number(idProject) ?? ""}
                                    onChange={(e) => {
                                        if (e) {
                                            navigate(`/PanelDeControlEmpresa/${idCompany}/${e}`)
                                        } else {
                                            navigate(`/PanelDeControlEmpresa/${idCompany}`)
                                        }
                                    }}
                                    placeholder="Seleccione un proyecto"
                                />

                                <div className="mt-4">
                                    <label className="block text-sm font-medium mb-1 dark:text-white">
                                        <FontAwesomeIcon icon={faCalendarAlt} className="mr-2" />
                                        Filtrar por fecha
                                    </label>

                                    <div className="flex space-x-4 mb-3">
                                        <label className="inline-flex items-center">
                                            <input
                                                type="radio"
                                                className="form-radio"
                                                name="dateType"
                                                value="single"
                                                checked={tempDateType === "single"}
                                                onChange={() => setTempDateType("single")}
                                            />
                                            <span className="ml-2 dark:text-white">Fecha única</span>
                                        </label>
                                        <label className="inline-flex items-center">
                                            <input
                                                type="radio"
                                                className="form-radio"
                                                name="dateType"
                                                value="range"
                                                checked={tempDateType === "range"}
                                                onChange={() => setTempDateType("range")}
                                            />
                                            <span className="ml-2 dark:text-white">Rango de fechas</span>
                                        </label>
                                    </div>

                                    {tempDateType === "single" && (
                                        <div className="grid grid-cols-2 gap-2">
                                            <div>
                                                <select
                                                    value={tempSelectedMonth}
                                                    onChange={(e) => setTempSelectedMonth(e.target.value)}
                                                    className="w-full p-2 text-sm border rounded dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                                                >
                                                    <option value="">Seleccione mes</option>
                                                    {months.map((month) => (
                                                        <option key={month.value} value={month.value}>
                                                            {month.label}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                            <div>
                                                <select
                                                    value={tempSelectedYear}
                                                    onChange={(e) => setTempSelectedYear(e.target.value)}
                                                    className="w-full p-2 text-sm border rounded dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                                                >
                                                    <option value="">Seleccione año</option>
                                                    {generateYears().map((year) => (
                                                        <option key={year} value={year}>
                                                            {year}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>
                                    )}

                                    {tempDateType === "range" && (
                                        <div className="space-y-2">
                                            <div className="grid grid-cols-2 gap-2">
                                                <div>
                                                    <select
                                                        value={tempRangeStartMonth}
                                                        onChange={(e) => setTempRangeStartMonth(e.target.value)}
                                                        className="w-full p-2 text-sm border rounded dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                                                    >
                                                        <option value="">Mes inicio</option>
                                                        {months.map((month) => (
                                                            <option key={`start-${month.value}`} value={month.value}>
                                                                {month.label}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>
                                                <div>
                                                    <select
                                                        value={tempRangeStartYear}
                                                        onChange={(e) => setTempRangeStartYear(e.target.value)}
                                                        className="w-full p-2 text-sm border rounded dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                                                    >
                                                        <option value="">Año inicio</option>
                                                        {generateYears().map((year) => (
                                                            <option key={`start-${year}`} value={year}>
                                                                {year}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>
                                            </div>
                                            <div className="grid grid-cols-2 gap-2">
                                                <div>
                                                    <select
                                                        value={tempRangeEndMonth}
                                                        onChange={(e) => setTempRangeEndMonth(e.target.value)}
                                                        className="w-full p-2 text-sm border rounded dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                                                    >
                                                        <option value="">Mes fin</option>
                                                        {months.map((month) => (
                                                            <option key={`end-${month.value}`} value={month.value}>
                                                                {month.label}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>
                                                <div>
                                                    <select
                                                        value={tempRangeEndYear}
                                                        onChange={(e) => setTempRangeEndYear(e.target.value)}
                                                        className="w-full p-2 text-sm border rounded dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                                                    >
                                                        <option value="">Año fin</option>
                                                        {generateYears().map((year) => (
                                                            <option key={`end-${year}`} value={year}>
                                                                {year}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    <div className="flex justify-between mt-3">
                                        <button
                                            onClick={applyDateFilters}
                                            disabled={
                                                !(
                                                    (tempDateType === "single" && (tempSelectedMonth || tempSelectedYear)) ||
                                                    (tempDateType === "range" &&
                                                        (tempRangeStartMonth || tempRangeStartYear || tempRangeEndMonth || tempRangeEndYear)) ||
                                                    tempSectorId ||
                                                    tempCompanyTypeId ||
                                                    tempStateId ||
                                                    tempCityId ||
                                                    (tempDistrictId && [1, 6].includes(getSession()?.type_id))
                                                )
                                            }
                                            className="px-3 py-1 bg-turquesa text-white text-sm rounded hover:bg-darkTurquesa disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            Aplicar filtros
                                        </button>
                                        <button
                                            onClick={clearFilters}
                                            className="px-3 py-1 bg-gray-300 text-gray-700 text-sm rounded hover:bg-gray-400 dark:bg-gray-600 dark:text-white"
                                        >
                                            Limpiar filtros
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}

                        <h1 className="px-2 text-sm font-medium dark:text-white mt-5">Últimos reportes</h1>
                        <div className="h-2/5 mb-8 overflow-y-auto">
                            {loading ? (
                                [1, 2, 3, 4, 5].map((item, index) => (
                                    <div
                                        key={index}
                                        className="bg-gray-200 dark:bg-gray-200 rounded-lg m-2 flex flex-row justify-between items-center animate-pulse"
                                    >
                                        <div className="p-2">
                                            <div className="h-4 bg-gray-100 dark:bg-gray-400 rounded w-1/4 mb-1"></div>
                                            <div className="h-3 bg-gray-100 dark:bg-gray-400 rounded w-full"></div>
                                        </div>
                                        <div className="flex space-x-2 p-2">
                                            <div className="h-6 w-6 bg-gray-100 dark:bg-gray-400 rounded"></div>
                                        </div>
                                    </div>
                                ))
                            ) : reports.length > 0 ? (
                                reports.map((report, index) => (
                                    <div
                                        onClick={() => getReport(report)}
                                        key={index}
                                        className="bg-slate-100 dark:bg-[#292929] rounded-lg m-2 flex flex-row justify-between items-center cursor-pointer transition-colors hover:bg-slate-300"
                                    >
                                        <div className="p-2">
                                            <h1 className="text-sm font-semibold dark:text-white">Reporte de {report.report_created_date}</h1>
                                            <p className="text-xs text-gray-500 dark:text-stone-400">Reporte {report.id}</p>
                                        </div>
                                        <div className="flex space-x-2 p-2">
                                            <FontAwesomeIcon icon={faEye} className="text-turquesa text-lg" />
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <div className="bg-slate-100 dark:bg-[#292929] rounded-lg m-2 flex flex-row justify-between items-center cursor-pointer transition-colors hover:bg-slate-300">
                                    <div className="p-2">
                                        <h1 className="text-sm font-semibold dark:text-white">No hay reportes</h1>
                                        <p className="text-xs text-gray-500 dark:text-stone-400">
                                            No se han registrado reportes en la plataforma
                                        </p>
                                    </div>
                                </div>
                            )}
                        </div>

                        <div
                            className="mt-5 p-5 chart-container"
                            ref={chartComponentRef}
                            style={{ visibility: "visible", opacity: 1, display: "block" }}
                        >
                            <ChartComponent rawData={resultsGrouped} />
                        </div>
                    </div>
                </section>
            )}

            {isMobileMenuOpen && (
                <div className="fixed inset-0 z-50 bg-white dark:bg-[#292929] lg:hidden">
                    <div className="flex justify-end p-4">
                        <button className="p-2 text-gray-500" onClick={() => setIsMobileMenuOpen(false)}>
                            <FontAwesomeIcon icon={faX} className="text-xl" />
                        </button>
                    </div>
                    <div className="flex flex-col lg:h-full justify-between overflow-y-auto p-4">
                        <div>
                            <div className="flex flex-row justify-start items-center pb-9">
                                <FontAwesomeIcon
                                    icon={faUserCircle}
                                    className="text-gray-500 dark:text-white text-2xl rounded-full w-9 h-9 p-1"
                                />
                                <div className="p-1">
                                    <span>
                                        <h1 className="text-sm font-medium dark:text-white">{userInfo.userName}</h1>
                                    </span>
                                    <span>
                                        <h1 className="text-xs dark:text-stone-400">{userInfo.role}</h1>
                                    </span>
                                </div>
                            </div>
                            <div className="flex flex-col justify-center">
                                <Link to="/PanelDeControl" onClick={() => setIsMobileMenuOpen(false)}>
                                    <h1 className="text-xs font-medium flex items-end mb-1 w-full text-gray-500 bg-transparent py-2 px-2 rounded-lg cursor-pointer transition-colors dark:text-stone-400 hover:bg-turquesa hover:text-white">
                                        <FontAwesomeIcon icon={faChartPie} className="text-xl pr-2 dark:text-white" />
                                        Dashboard
                                    </h1>
                                </Link>
                                {userInfo.role === "Administrador" && (
                                    <Link to="/Organizaciones" onClick={() => setIsMobileMenuOpen(false)}>
                                        <h1 className="text-xs font-medium flex items-end mb-1 w-full text-gray-500 bg-transparent py-2 px-2 rounded-lg cursor-pointer transition-colors dark:text-stone-400 hover:bg-turquesa hover:text-white">
                                            <FontAwesomeIcon icon={faBuilding} className="text-xl pr-2 dark:text-white" />
                                            Organizaciones
                                        </h1>
                                    </Link>
                                )}

                                {userInfo.role === "Organización" && (
                                    <Link to="/Empleados" onClick={() => setIsMobileMenuOpen(false)}>
                                        <h1 className="text-xs font-medium flex items-end mb-1 w-full text-gray-500 bg-transparent py-2 px-2 rounded-lg cursor-pointer transition-colors dark:text-stone-400 hover:bg-turquesa hover:text-white">
                                            <FontAwesomeIcon icon={faBuilding} className="text-xl pr-2 dark:text-white" />
                                            Usuarios
                                        </h1>
                                    </Link>
                                )}
                                <Link to="/Reportes" onClick={() => setIsMobileMenuOpen(false)}>
                                    <h1 className="text-xs font-medium flex items-end mb-1 w-full text-gray-500 bg-transparent py-2 px-2 rounded-lg cursor-pointer transition-colors dark:text-stone-400 hover:bg-turquesa hover:text-white">
                                        <FontAwesomeIcon icon={faFolder} className="text-xl pr-2 dark:text-white" />
                                        Reportes
                                    </h1>
                                </Link>
                                {userInfo.role === "Administrador" && (
                                    <Link to="/CompararProyectos">
                                        <h1 className="text-xs font-medium flex items-end mb-1 w-full text-gray-500 bg-transparent py-2 px-2 rounded-lg cursor-pointer transition-colors dark:text-stone-400 hover:bg-turquesa hover:text-white">
                                            <FontAwesomeIcon icon={faChartBar} className="text-xl pr-2 dark:text-white" />
                                            Estadísticas de proyectos
                                        </h1>
                                    </Link>
                                )}
                            </div>
                        </div>
                        <div className="flex flex-col justify-start">
                            {[4].includes(getSession()?.type_id) && (
                                <Link
                                    to="/PerfilDeLaOrganizacion"
                                    className="text-xs font-medium items-end mb-1 w-full text-gray-500 bg-transparent py-2 px-2 rounded-lg cursor-pointer transition-colors dark:text-stone-400 hover:bg-orange-500 hover:text-white"
                                    onClick={() => setIsMobileMenuOpen(false)}
                                >
                                    <FontAwesomeIcon icon={faCog} className="text-xl pr-2 dark:text-white" />
                                    Ajustes
                                </Link>
                            )}
                            <button type="button" className="text-start" onClick={handleLogout}>
                                <h1 className="text-xs font-medium items-end mb-1 w-full text-gray-500 bg-transparent py-2 px-2 rounded-lg cursor-pointer transition-colors dark:text-stone-400 hover:bg-red-500 hover:text-white">
                                    <FontAwesomeIcon icon={faSignOutAlt} className="text-xl pr-2 dark:text-white" />
                                    Cerrar sesión
                                </h1>
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}

export default MainThemeLayoutCompany;
